import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Form, useFormikContext } from 'formik'

import FormikLabeledTextField from '~/components/formikFields/formikLabeledTextField'
import FormikServiceAutocomplete from '~/components/formikServiceAutocomplete'
import { EAgentKeys, TCreateAssistant } from '~/features/agents/models'
import LocationsAutocomplete from '~/features/locations/components/locationsAutocomplete'
import { ETestId } from '~/models/tests'
import rem from '~/utils/ui'

type TProps = {
  isCreating: boolean
}

function CreateFormCard({ isCreating }: TProps) {
  const theme = useTheme()
  const formik = useFormikContext<TCreateAssistant>()

  return (
    <Box
      sx={{
        padding: rem(24),
        backgroundColor: theme.palette.background.paper,
        borderRadius: rem(16),
        boxShadow: theme.shadows[2],
        display: 'flex',
        flexDirection: 'column',
        order: { xxxs: -1, md: 0 },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: rem(20),
          color: theme.palette.text.primary,
          marginBottom: rem(20),
        }}
      >
        Create your first AI Assistant
      </Typography>

      <Form
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: rem(24),
        }}
      >
        <Stack spacing={rem(24)}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: rem(16),
            }}
          >
            <FormikLabeledTextField
              inputProps={{
                'data-testid': ETestId.CreateAssistantReferenceName,
              }}
              label="Reference Name"
              name={EAgentKeys.agentReferenceName}
              placeholder="e.g. Seattle Assistant"
              required
              size="medium"
              withTextFieldLabel
            />
            <FormikLabeledTextField
              inputProps={{
                'data-testid': ETestId.CreateAssistantBusinessName,
              }}
              label="Business Name"
              name={EAgentKeys.businessName}
              placeholder="e.g. Emerald City Plumbing"
              required
              size="medium"
              withTextFieldLabel
            />
            <FormikServiceAutocomplete
              name={EAgentKeys.service}
              required
              withInternalLabel
            />
            <LocationsAutocomplete
              label="Location"
              name={EAgentKeys.locationId}
              placeholder="Select location..."
              required
              withInternalLabel
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              type="submit"
              disabled={!formik.isValid || isCreating}
              data-testid={ETestId.CreateAssistantSubmit}
              sx={{
                width: { xxxs: '100%', md: 'fit-content' },
                padding: `${rem(8)} ${rem(16)}`,
                fontSize: rem(14),
                fontWeight: 600,
                backgroundColor: theme.palette.primaryButtonBg,
                color: theme.palette.primaryButtonFont,
                borderRadius: rem(100),
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.primaryButtonBg,
                  opacity: 0.9,
                },
              }}
            >
              {isCreating ? (
                <>
                  <CircularProgress
                    size={rem(16)}
                    sx={{
                      color: theme.palette.primaryButtonFont,
                      marginRight: rem(8),
                    }}
                  />
                  Creating...
                </>
              ) : (
                'Create Assistant'
              )}
            </Button>
          </Box>
        </Stack>
      </Form>
    </Box>
  )
}

export default CreateFormCard
